import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, XCircle, Clock, DollarSign, User, FileText, Eye, ExternalLink, MessageSquare, HelpCircle, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  paid: 'bg-emerald-100 text-emerald-800',
  payment_pending: 'bg-orange-100 text-orange-800',
  payment_failed: 'bg-red-100 text-red-800',
  disputed: 'bg-purple-100 text-purple-800'
};

const typeLabels = {
  fuel: 'Fuel',
  tolls: 'Tolls',
  cleaning: 'Cleaning',
  late_return: 'Late Return',
  damage: 'Damage',
  other: 'Other'
};

export default function AdminClaims() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState(''); // Default to 'all'

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchClaims = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filter ? `${API_URL}/api/admin/claims?status=${filter}` : `${API_URL}/api/admin/claims`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClaims(data.claims || []);
      }
    } catch (error) {
      console.error('Failed to fetch claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClaimDetail = async (claimId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/claims/${claimId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedClaim(data);
      }
    } catch (error) {
      console.error('Failed to fetch claim detail:', error);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, [filter]);

  const handleApprove = async () => {
    if (!selectedClaim) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/claims/${selectedClaim.claim_id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          review_note: reviewNote,
          decision_reason_code: 'approved_full'
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Claim approved - ${data.payment_result?.method || 'pending payment'}`);
        setShowApproveDialog(false);
        setSelectedClaim(null);
        setReviewNote('');
        fetchClaims();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve claim');
      }
    } catch (error) {
      toast.error('Failed to approve claim');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedClaim) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/claims/${selectedClaim.claim_id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          review_note: reviewNote,
          decision_reason_code: 'rejected'
        })
      });
      if (res.ok) {
        toast.success('Claim rejected');
        setShowRejectDialog(false);
        setSelectedClaim(null);
        setReviewNote('');
        fetchClaims();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reject claim');
      }
    } catch (error) {
      toast.error('Failed to reject claim');
    } finally {
      setProcessing(false);
    }
  };

  // P0: Retry payment for failed claims
  const handleRetryPayment = async () => {
    if (!selectedClaim) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/claims/${selectedClaim.claim_id}/retry-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_note: reviewNote || 'Payment retry initiated'
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_result?.success) {
          toast.success(`Payment succeeded via ${data.payment_result.method}`);
        } else {
          toast.warning(`Payment retry failed: ${data.payment_result?.error || 'Unknown error'}`);
        }
        setSelectedClaim(null);
        setReviewNote('');
        fetchClaims();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to retry payment');
      }
    } catch (error) {
      toast.error('Failed to retry payment');
    } finally {
      setProcessing(false);
    }
  };

  // P0: Resolve claim offline (manual settlement)
  const [showResolveOfflineDialog, setShowResolveOfflineDialog] = useState(false);
  const [resolutionMethod, setResolutionMethod] = useState('offline_payment');
  const [externalReference, setExternalReference] = useState('');

  const handleResolveOffline = async () => {
    if (!selectedClaim) return;
    if (!reviewNote || reviewNote.length < 10) {
      toast.error('Resolution note must be at least 10 characters (audit requirement)');
      return;
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/claims/${selectedClaim.claim_id}/resolve-offline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resolution_note: reviewNote,
          resolution_method: resolutionMethod,
          external_reference: externalReference || null
        })
      });
      if (res.ok) {
        toast.success('Claim resolved offline');
        setShowResolveOfflineDialog(false);
        setSelectedClaim(null);
        setReviewNote('');
        setResolutionMethod('offline_payment');
        setExternalReference('');
        fetchClaims();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to resolve claim');
      }
    } catch (error) {
      toast.error('Failed to resolve claim');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PlatformOwnerAppShell title="Claims Management" activeId="claims">
      <div className="bg-white/5 rounded-xl p-4 sm:p-6" data-testid="admin-claims-page">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['draft', 'submitted', 'under_review', 'approved', 'rejected', 'disputed', 'payment_failed', 'paid', ''].map((status) => (
            <Button
              key={status || 'all'}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-purple-600 hover:bg-purple-700' : 'border-white/20 text-white/70 hover:bg-white/10'}
              data-testid={`filter-${status || 'all'}`}
            >
              {status ? status.replace('_', ' ').toUpperCase() : 'ALL'}
            </Button>
          ))}
        </div>

        {/* Claims List */}
        {loading ? (
          <div className="text-center py-12 text-white/60">Loading claims...</div>
        ) : claims.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center text-white/50">
              No claims found
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {claims.map((claim) => (
              <Card key={claim.claim_id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors" data-testid={`claim-card-${claim.claim_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={statusColors[claim.status]}>
                          {claim.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <Badge variant="outline">{typeLabels[claim.claim_type] || claim.claim_type}</Badge>
                      </div>
                      <div className="text-lg font-semibold text-green-600 mb-1">
                        ${(claim.amount_cents / 100).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        {claim.description?.substring(0, 100)}{claim.description?.length > 100 ? '...' : ''}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-4">
                        <span><User className="w-3 h-3 inline mr-1" />{claim.host?.full_name || 'Host'}</span>
                        <span><FileText className="w-3 h-3 inline mr-1" />{claim.booking?.booking_id?.substring(0, 12)}</span>
                        <span><Clock className="w-3 h-3 inline mr-1" />{new Date(claim.submitted_at || claim.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchClaimDetail(claim.claim_id)}
                        data-testid={`view-claim-${claim.claim_id}`}
                      >
                        View Details
                      </Button>
                      {claim.status === 'submitted' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowApproveDialog(true);
                            }}
                            data-testid={`approve-claim-${claim.claim_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowRejectDialog(true);
                            }}
                            data-testid={`reject-claim-${claim.claim_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Claim Detail Dialog */}
        <Dialog open={!!selectedClaim && !showApproveDialog && !showRejectDialog} onOpenChange={() => setSelectedClaim(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Claim Details
                <Badge className={statusColors[selectedClaim?.status]}>{selectedClaim?.status?.replace('_', ' ').toUpperCase()}</Badge>
              </DialogTitle>
            </DialogHeader>
            {selectedClaim && (
              <div className="space-y-6">
                {/* Claim Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Claim ID</div>
                    <div className="font-mono text-sm">{selectedClaim.claim_id?.substring(0, 12)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Type</div>
                    <Badge variant="outline">{typeLabels[selectedClaim.claim_type]}</Badge>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Amount</div>
                    <div className="text-xl font-bold text-green-600">${(selectedClaim.amount_cents / 100).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Created</div>
                    <div className="text-sm">{new Date(selectedClaim.created_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Description
                  </h3>
                  <div className="bg-gray-50 p-3 rounded text-sm border border-gray-200">{selectedClaim.description || 'No description provided'}</div>
                </div>

                {/* Parties */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded border border-blue-100">
                    <div className="text-xs text-blue-600 uppercase mb-1">Host (Claimant)</div>
                    <div className="font-medium">{selectedClaim.host?.full_name}</div>
                    <div className="text-xs text-gray-500">{selectedClaim.host?.email}</div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded border border-purple-100">
                    <div className="text-xs text-purple-600 uppercase mb-1">Renter (Charged)</div>
                    <div className="font-medium">{selectedClaim.renter?.full_name || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{selectedClaim.renter?.email}</div>
                  </div>
                </div>

                {/* Booking Info */}
                {selectedClaim.booking && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <Eye className="w-4 h-4" /> Booking Timeline
                    </h3>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Booking ID:</span>
                        <a href={`/bookings/${selectedClaim.booking.booking_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          {selectedClaim.booking.booking_id?.substring(0, 12)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Status:</span>
                        <Badge variant="outline">{selectedClaim.booking.status}</Badge>
                      </div>
                      {selectedClaim.booking.start_date && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Trip:</span>
                          <span>{new Date(selectedClaim.booking.start_date).toLocaleDateString()} - {new Date(selectedClaim.booking.end_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Fee Breakdown */}
                {selectedClaim.claim_type && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> Fee Breakdown
                    </h3>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <div className="flex justify-between text-sm py-1 border-b border-gray-200">
                        <span>Claim Amount</span>
                        <span className="font-medium">${(selectedClaim.amount_cents / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-gray-200">
                        <span>Platform Fee</span>
                        <span className="text-gray-500">$0.00</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-gray-200">
                        <span className="text-gray-500">Processing Fee (est.)</span>
                        <span className="text-gray-500">~${(selectedClaim.amount_cents * 0.029 / 100 + 0.30).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 font-medium">
                        <span>Host Receives</span>
                        <span className="text-green-600">${(selectedClaim.amount_cents / 100).toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        * Claims have 0% platform fee. Processing fees are passed to the renter.
                      </p>
                    </div>
                  </div>
                )}

                {/* Evidence Files */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Image className="w-4 h-4" /> Evidence ({selectedClaim.evidence_files?.length || 0} files)
                  </h3>
                  {selectedClaim.evidence_files?.length > 0 ? (
                    <div className="flex gap-2 flex-wrap">
                      {selectedClaim.evidence_urls?.map((ev, idx) => (
                        <a 
                          key={idx} 
                          href={ev.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="px-3 py-2 bg-blue-50 text-blue-600 text-sm rounded border border-blue-200 hover:bg-blue-100 flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          Evidence {idx + 1}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">No evidence files attached</div>
                  )}
                </div>

                {/* Audit Trail */}
                {selectedClaim.audit_trail?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Audit Trail
                    </h3>
                    <div className="space-y-1 max-h-40 overflow-y-auto bg-gray-50 p-2 rounded border border-gray-200">
                      {selectedClaim.audit_trail.map((log, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded flex justify-between items-center border border-gray-100">
                          <span className="font-medium">{log.action}</span>
                          <span className="text-gray-400">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {(selectedClaim.status === 'submitted' || selectedClaim.status === 'under_review') && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => setShowApproveDialog(true)}
                      data-testid="approve-claim-detail-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve Claim
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        // Mark as disputed / request more info
                        toast.info('Request more info - coming soon');
                      }}
                      data-testid="request-info-btn"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" /> Request Info
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setShowRejectDialog(true)}
                      data-testid="reject-claim-detail-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </div>
                )}

                {/* Payment Failed Actions - Retry or Resolve Offline */}
                {selectedClaim.status === 'payment_failed' && (
                  <div className="space-y-3 pt-4 border-t">
                    {/* Payment Error Display */}
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <div className="text-xs text-red-600 uppercase font-medium mb-1">Payment Failed</div>
                      <div className="text-sm text-red-800">{selectedClaim.payment_error || 'Unknown error'}</div>
                      {selectedClaim.stripe_payment_intent_id && (
                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                          <span>Payment Intent: {selectedClaim.stripe_payment_intent_id}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={handleRetryPayment}
                        disabled={processing}
                        data-testid="retry-payment-btn"
                      >
                        {processing ? 'Processing...' : 'Retry Payment'}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowResolveOfflineDialog(true)}
                        data-testid="resolve-offline-btn"
                      >
                        Resolve Offline
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Claim</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded">
                <div className="text-sm text-gray-600">Approving claim for</div>
                <div className="text-2xl font-bold text-green-600">
                  ${(selectedClaim?.amount_cents / 100).toFixed(2)}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Review Note</label>
                <Textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Add a note about why this claim is approved..."
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={processing}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={processing}>
                {processing ? 'Processing...' : 'Approve & Process Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Claim</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div className="text-sm text-red-800">
                  This will reject the claim. The host will be notified.
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Rejection Reason (required)</label>
                <Textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Explain why this claim is being rejected..."
                  className="mt-1"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processing}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={processing || !reviewNote}>
                {processing ? 'Processing...' : 'Reject Claim'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resolve Offline Dialog */}
        <Dialog open={showResolveOfflineDialog} onOpenChange={setShowResolveOfflineDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolve Claim Offline</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded border border-amber-200">
                <div className="text-sm text-amber-800">
                  Use this when payment was collected manually, waived, or settled externally.
                  This action is for audit compliance and requires a detailed note.
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Resolution Method</label>
                <select
                  value={resolutionMethod}
                  onChange={(e) => setResolutionMethod(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="resolution-method-select"
                >
                  <option value="offline_payment">Offline Payment (cash/bank transfer)</option>
                  <option value="waived">Waived by Admin</option>
                  <option value="settled_externally">Settled Externally</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">External Reference (optional)</label>
                <input
                  type="text"
                  value={externalReference}
                  onChange={(e) => setExternalReference(e.target.value)}
                  placeholder="Invoice #, bank transfer reference, etc."
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="external-reference-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Resolution Note (required - min 10 chars)</label>
                <Textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Detailed explanation of how this claim was resolved..."
                  className="mt-1"
                  required
                  data-testid="resolution-note-input"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {reviewNote.length}/10 characters minimum
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolveOfflineDialog(false)} disabled={processing}>
                Cancel
              </Button>
              <Button onClick={handleResolveOffline} disabled={processing || reviewNote.length < 10}>
                {processing ? 'Processing...' : 'Resolve Offline'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PlatformOwnerAppShell>
  );
}
